import { Box, Stack, Typography, Button, Chip, Tooltip } from "@mui/material";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import React from "react";
import { Tenant } from "../../types";
import { ConfirmationDialog } from "../../widgets/ConfirmationDialog/ConfirmationDialog";

interface HoldingAreaListItemProps {
  tenant: Tenant;
  onAssign: (tenantId: string) => void;
  onEdit: (tenant: Tenant) => void;
  onRemove: (tenantId: string) => void;
  disabled?: boolean;
}

/**
 * Individual list item for aircraft in holding area
 * Shows thumbnail, tail number, model, and assign button
 */
export const HoldingAreaListItem: React.FC<HoldingAreaListItemProps> = ({
  tenant,
  onAssign,
  onEdit,
  onRemove,
  disabled = false,
}) => {
  const [imageError, setImageError] = React.useState(false);
  const [noteViewed, setNoteViewed] = React.useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);

  const handleRemoveClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmRemove = () => {
    setConfirmDialogOpen(false);
    onRemove(tenant.id);
  };

  const handleCancelRemove = () => {
    setConfirmDialogOpen(false);
  };

  return (
    <Box sx={{ position: "relative" }}>
      {/* Note Badge - appears at top-left corner */}
      {tenant.note && !noteViewed && (
        <Box
          sx={{
            position: "absolute",
            top: -6,
            left: -6,
            width: 12,
            height: 12,
            borderRadius: "50%",
            backgroundColor: "#b89230",
            border: "2px solid white",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            zIndex: 10,
          }}
        />
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: 2,
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
      {/* Aircraft Thumbnail */}
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: 1,
          backgroundColor: "grey.200",
          flexShrink: 0,
          mr: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {tenant.aircraft?.image && !imageError ? (
          <img
            src={tenant.aircraft.image}
            alt={tenant.tail_number}
            onError={() => setImageError(true)}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: 8,
            }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            N/A
          </Typography>
        )}
      </Box>

      {/* Aircraft Info */}
      <Stack flex={1} spacing={0.5}>
        {/* Header: Tail Number + Type Chip */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            sx={{ fontSize: "0.95rem", lineHeight: 1.2 }}
          >
            {tenant.tail_number}
          </Typography>
          <Chip
            label={tenant.type === "transient" ? "Transient" : "Base"}
            size="small"
            color={tenant.type === "transient" ? "primary" : "secondary"}
            variant="outlined"
            sx={{ fontSize: "0.65rem", fontWeight: 500, height: 20 }}
          />
          {tenant.note && (
            <Tooltip
              title={tenant.note}
              arrow
              placement="top"
              onOpen={() => setNoteViewed(true)}
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: '#f2f2f2',
                    color: '#000000',
                    fontSize: '12px',
                    borderRadius: '5px',
                    padding: '8px 12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    maxWidth: 250,
                    '&::before': {
                      backgroundColor: '#b89230',
                      content: '""',
                      display: 'block',
                      height: '4px',
                      margin: '-8px -12px 6px -12px',
                      borderTopLeftRadius: '5px',
                      borderTopRightRadius: '5px',
                    }
                  }
                },
                arrow: {
                  sx: {
                    color: '#f2f2f2',
                  }
                }
              }}
            >
              <StickyNote2Icon
                sx={{
                  fontSize: 16,
                  color: 'warning.main',
                  cursor: 'help'
                }}
              />
            </Tooltip>
          )}
        </Stack>

        {/* Aircraft Model - Full Width */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.8rem", lineHeight: 1.2 }}
        >
          {tenant.aircraft?.model || "Unknown Model"}
        </Typography>
      </Stack>

      {/* Action Buttons */}
      <Stack spacing={0.5} sx={{ minWidth: 70 }}>
        <Button
          variant="contained"
          size="small"
          onClick={() => onAssign(tenant.id)}
          disabled={disabled}
          sx={{ py: 0.25, fontSize: "0.7rem", minHeight: 24 }}
        >
          Assign
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => onEdit(tenant)}
          disabled={disabled}
          sx={{ py: 0.25, fontSize: "0.7rem", minHeight: 24 }}
        >
          Edit
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="error"
          onClick={handleRemoveClick}
          disabled={disabled}
          sx={{ py: 0.25, fontSize: "0.7rem", minHeight: 24 }}
        >
          Remove
        </Button>
      </Stack>
      </Box>

      {/* Confirmation Dialog */}
      {confirmDialogOpen && (
        <ConfirmationDialog
          title="Remove Aircraft from Holding Area?"
          text={`Are you sure you want to remove ${tenant.tail_number || tenant.aircraft?.model || "this aircraft"} from the holding area?`}
          okText="Remove"
          cancelText="Cancel"
          okButtonColor="error"
          onOk={handleConfirmRemove}
          onCancel={handleCancelRemove}
          onClose={handleCancelRemove}
        />
      )}
    </Box>
  );
};
